<template>
  <div class="debug-info">
    <div class="debug-info__ota">
      <h3>{{ t('app.debug.ota') }}</h3>
      <pre>Current status: {{ otaSetting.ota ? 'Enabled' : 'Disabled' }}</pre>
      <BfButton
        v-if="!otaSetting.ota"
        :type="ButtonType.Outlined"
        @click="updateOta(true)"
      >
        {{ t('app.fitbike.ota.enable') }}
      </BfButton>
      <BfButton
        v-else
        :type="ButtonType.Outlined"
        @click="updateOta(false)"
      >
        {{ t('app.fitbike.ota.disable') }}
      </BfButton>
    </div>

    <h3>{{ t('app.debug.mqttStore') }}</h3>
    <pre>connected: {{ connected }}</pre>

    <h3>{{ t('app.debug.bikeStore') }}</h3>
    <pre>hasFitbike: {{ hasFitbike }}</pre>
    <pre>hasPositioningMotors: {{ hasPositioningMotors }}</pre>
    <pre>hasXYTool: {{ hasXYTool }}</pre>

    <h3>{{ t('app.debug.positionStore') }}</h3>
    <h4>isCalibrated</h4>
    <pre>{{ isCalibrated }}</pre>
    <h4>isLatestMeasurementCalibrated</h4>
    <pre>{{ isLatestMeasurementCalibrated }}</pre>
    <h4>startPosition</h4>
    <pre>{{ startPosition }}</pre>
    <h4>positionOffset</h4>
    <pre>{{ positionOffset }}</pre>
    <h4>positionOffsetGear</h4>
    <pre>{{ positionOffsetGear }}</pre>
    <h4>positionDeviation</h4>
    <pre>{{ positionDeviation }}</pre>
    <h4>motorPosition</h4>
    <pre>{{ motorPosition }}</pre>
    <h4>measuredPosition</h4>
    <pre>{{ measuredPosition }}</pre>
    <h4>absolutePosition</h4>
    <pre>{{ absolutePosition }}</pre>
    <h4>positionChange</h4>
    <pre>{{ positionChange }}</pre>

    <h3>{{ t('app.debug.measurementStore') }}</h3>
    <pre>measurements ({{ measurements.length }}): {{ measurements }}</pre>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { BfButton, ButtonType } from '@finetic/bikefitting-ui'
import { storeToRefs } from 'pinia'
import { useMqttStore } from '@/store/mqttStore'
import { usePreferenceStore } from '@/store/preferenceStore'
import { usePositionStore } from '@/store/positionStore'
import { useMeasurementStore } from '@/store/measurementStore'
import { useI18n } from 'vue-i18n'
import { MqttTopic } from '@/mqttDefinitions/topics'

const { t } = useI18n()

const mqttStore = useMqttStore()
const preferenceStore = usePreferenceStore()
const positionStore = usePositionStore()
const measurementStore = useMeasurementStore()

const { client, connected, doSubscribe, doRawPublish } = mqttStore
const { getRawValueOnTopic } = storeToRefs(mqttStore)
const { hasFitbike, hasPositioningMotors, hasXYTool } = preferenceStore
const {
  isCalibrated,
  isLatestMeasurementCalibrated,
  startPosition,
  positionOffset,
  positionOffsetGear,
  positionDeviation,
  motorPosition,
  measuredPosition,
  absolutePosition,
  positionChange,
} = positionStore
const { measurements } = measurementStore

const otaTopic = MqttTopic.CONTROLLER_OTA_UPDATE
const otaSetting = computed(
  () => (getRawValueOnTopic.value(otaTopic) as any) ?? {},
)

const updateOta = (enable: boolean) => {
  doRawPublish(
    enable ? MqttTopic.CONTROLLER_OTA_SET : MqttTopic.CONTROLLER_OTA_UNSET,
    JSON.stringify({}),
    client,
  )
}

doSubscribe(otaTopic, client)
</script>

<style lang="scss">
.debug-info {
  padding: 16px;

  h4 {
    color: rgb(0, 0, 0, 0.4);
    margin-bottom: 0;
    padding-bottom: 0;
    text-transform: none;
  }

  pre {
    margin-top: 0;
    font-size: 0.8em;
  }

  &__ota {
    margin-bottom: 24px;

    & > div {
      width: 100%;
    }
  }
}
</style>
